/**
 * CR413/CR415: Promote Config Modal
 * 
 * Modal for promoting deal card config between environments.
 * CR415: All promotion targets enabled. Supports:
 *   Staging → Preview
 *   Preview → Production
 */

import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { ArrowRight, Upload, AlertTriangle } from "lucide-react";
import type { PublishEnvironment } from "@/hooks/usePromoteDealCardConfig";

interface PromoteConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealType: string;
  sourceEnvironment: PublishEnvironment;
  sourceVersionId: string;
  sourceTimestamp: string;
  onPromote: (targetEnv: PublishEnvironment) => Promise<void>;
  promoting: boolean;
  conflictError?: boolean;
}

/** Valid promotion paths */
const PROMOTION_TARGETS: Record<PublishEnvironment, { value: PublishEnvironment; label: string }[]> = {
  staging: [{ value: "preview", label: "Admin" }],
  preview: [{ value: "production", label: "Live" }],
  production: [], // no further promotion
};

const ENV_LABELS: Record<PublishEnvironment, string> = {
  staging: "Staging",
  preview: "Admin",
  production: "Live",
};

export function PromoteConfigModal({
  open,
  onOpenChange,
  dealType,
  sourceEnvironment,
  sourceVersionId,
  sourceTimestamp,
  onPromote,
  promoting,
  conflictError = false,
}: PromoteConfigModalProps) {
  const targets = PROMOTION_TARGETS[sourceEnvironment] ?? [];
  const [selectedTarget, setSelectedTarget] = useState<PublishEnvironment | "">("");

  // Reset selection when modal opens
  const handleOpenChange = (o: boolean) => {
    if (o) setSelectedTarget(targets.length === 1 ? targets[0].value : "");
    onOpenChange(o);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Promote Config
          </DialogTitle>
          <DialogDescription>
            Promote <strong>{dealType}</strong> config from {ENV_LABELS[sourceEnvironment]}.
          </DialogDescription>
        </DialogHeader>

        {/* Source info */}
        <div className="rounded-md border p-3 bg-muted/50 space-y-1 text-xs">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Source:</span>
            <Badge variant="secondary">{ENV_LABELS[sourceEnvironment]}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Version:</span>
            <span className="font-mono">{sourceVersionId.slice(0, 8)}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Published:</span>
            <span>{new Date(sourceTimestamp).toLocaleString()}</span>
          </div>
        </div>

        <ArrowRight className="h-4 w-4 mx-auto text-muted-foreground" />

        {/* Target selection */}
        {targets.length > 0 ? (
          <RadioGroup
            value={selectedTarget}
            onValueChange={(v) => setSelectedTarget(v as PublishEnvironment)}
          >
            {targets.map(env => (
              <div key={env.value} className="flex items-center gap-3 rounded-md border p-3">
                <RadioGroupItem value={env.value} id={`target-${env.value}`} />
                <Label htmlFor={`target-${env.value}`} className="flex-1">
                  {env.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        ) : (
          <p className="text-xs text-muted-foreground text-center py-2">
            No promotion targets available from {ENV_LABELS[sourceEnvironment]}.
          </p>
        )}

        {/* Conflict error */}
        {conflictError && (
          <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/10 p-2 rounded">
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
            <span>Target environment was updated since this modal opened. Close and retry to get the latest version.</span>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={promoting}>
            Cancel
          </Button>
          <Button
            onClick={() => selectedTarget && onPromote(selectedTarget as PublishEnvironment)}
            disabled={!selectedTarget || promoting || targets.length === 0}
          >
            {promoting ? "Promoting…" : `Promote → ${selectedTarget ? ENV_LABELS[selectedTarget as PublishEnvironment] : "…"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
